import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
  useContext,
} from 'react';
import { connect } from 'react-redux';
import { Drawer, Form, Space, Button, Radio, Input, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import IntlContext from '@/components/Intl/IntlContext';
import { AMOUNT_REG, SourceChannelType } from '@/config';
import * as CreditApi from '@/api/bill/credit';
import * as BillApi from '@/api/bill';
import usePortalConfirm from '@/components/PortalConfirm';
import SelectDiscountCoupon, { IselectDiscountCouponRef } from '@/components/SelectDiscountCoupon';
import { Icoupon } from '@/redux/reducers/guest';
import { parse2Number, parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import CouponService from '@/components/Reservation/CouponService';
import useUpdate from '@/hooks/useUpdate';
import { fetchCurrentSysTime } from '@/api/order';
import { format2DateStr } from '@/utils/date';

import { ROOM_RATE_PLUS_SUBJECTS } from '../config';
import { OpenRoomRatePlusDrawerProps, RoomRatePlusDrawerProps } from '../interface';
import commonStyles from '../common.module.scss';
import styles from './index.module.scss';

const { TextArea } = Input;

const RoomRatePlus: React.FC<RoomRatePlusDrawerProps> = forwardRef((props, ref) => {
  const { selectedRoom } = props;
  const [amountForm] = Form.useForm();

  const [resvNo, setResvNo] = useState(''); // 主订单号
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const confirmPortal = usePortalConfirm();
  const [masterInfo, setMasterInfo] = useState<{ wyuid: string }>(null);
  const [couponInfo, setCouponInfo] = useState<Partial<Icoupon>>(null);
  const selectCouponRef = useRef<IselectDiscountCouponRef>();
  const amountValue = Form.useWatch('amount', amountForm);
  const [currentChannel, setCurrentChannel] = useState<SourceChannelType>(null);
  const couponService = useMemo(() => {
    return new CouponService();
  }, []);
  const forceUpdate = useUpdate();
  const currentBusinessDate = useRef<number>();
  const intlCtx = useContext(IntlContext);

  useEffect(() => {
    if (drawerVisible) {
      BillApi.getSubOrderChannel({ recNo: selectedRoom.recNo }).then((res) => {
        setCurrentChannel(res.data);
      });
      fetchCurrentSysTime().then((res) => {
        currentBusinessDate.current = res.data.businessDate;
      });
    }
  }, [drawerVisible]);

  const openDrawer = (params: OpenRoomRatePlusDrawerProps) => {
    setResvNo(params.resvNo);
    setDrawerVisible(true);
    setCouponInfo(null);
    setMasterInfo(null);
    BillApi.fetchMasterInfoByOrderNo({ resvNo: params.resvNo })
      .then((res) => {
        couponService.initCouponList(res.data.wyuid).then(forceUpdate);
        setMasterInfo(res.data);
      })
      .catch(() => {
        setMasterInfo(null);
      });
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  const changeSubject = async (e) => {
    const code = e.target!.value;
    if (code === 'D03070001') {
      amountForm.setFieldsValue({
        amount: '',
      });
    } else {
      try {
        // 获取房价
        const { status, data } = await BillApi.fetchRoomRatebyDate({
          occType: selectedRoom.occType,
          roomTypeId: selectedRoom.roomTypeId,
        });
        if (status) {
          const rate = data?.realPrice;
          amountForm.setFieldsValue({
            amount: parse2Number(
              parseFen2Yuan(code === 'D03050001' ? rate : (rate / 2).toFixed(2)),
            ),
          });
        } else {
          amountForm.setFieldsValue({
            amount: '',
          });
        }
      } catch (err) {
        amountForm.setFieldsValue({
          amount: '',
        });
      }
    }
  };

  useEffect(() => {
    setCouponInfo(null);
  }, [amountValue]);

  // 选择优惠券
  const showCouponSelector = () => {
    amountForm.validateFields(['subjectCode', 'amount']).then(
      () => {
        const { subjectCode, amount } = amountForm.getFieldsValue();
        const targetSubject = ROOM_RATE_PLUS_SUBJECTS.find((item) => item.value === subjectCode);
        if (targetSubject) {
          selectCouponRef.current.openModal({
            wyuid: masterInfo.wyuid,
            channelType: currentChannel,
            amount: parse2Number(parseYuan2Fen(amount)) || 0,
            occType: targetSubject.subjectOccType,
            id: null,
            changedCouponId: [],
            xuanzhu: { remark: intl.get('bill_room_rate_plus_double_add_room_fee') },
            businessDate: format2DateStr(currentBusinessDate.current),
            callback: (_, [targetCoupon]) => {
              setCouponInfo(targetCoupon);
            },
          });
        }
      },
      () => {
        //
      },
    );
  };

  // 优惠券信息
  const renderCouponInfo = () => {
    if (couponInfo && couponInfo.couponNo) {
      return (
        <div className={styles.couponContent}>
          <span>{`${couponInfo.couponName} ${parseFen2Yuan(-couponInfo.couponFaceValue)}`}</span>
          <CloseOutlined className={styles.couponClearIcon} onClick={() => setCouponInfo(null)} />
        </div>
      );
    }

    // 必填值
    const requiredValue =
      amountForm.getFieldValue('subjectCode') && amountForm.getFieldValue('amount');

    // 是否用券
    const couponDisabled =
      couponService.isCouponDisabled({
        priceChannel: currentChannel,
        exclude: [],
      }) || !requiredValue;

    return (
      <Button
        type="link"
        className={`${styles.couponSelectorBtn} ${
          couponDisabled ? styles.disabledSelectorBtn : null
        }`}
        onClick={showCouponSelector}
      >
        {intl.get('bill_room_rate_plus_choose_coupon')}
      </Button>
    );
  };

  // 二次确认
  const showConfirmModal = () => {
    return new Promise<void>((resolve, reject) => {
      confirmPortal.showDrawer({
        title: intl.get('bill_room_rate_plus_add_room_fee'),
        content: (
          <div>
            <span>{intl.get('bill_room_rate_plus_add_room_fee_tips')}</span>
            <span style={{ color: '#FF6463' }}>
              {intl.get('bill_room_rate_plus_use_coupon_tips1')}
            </span>
            <span>{intl.get('bill_room_rate_plus_use_coupon_tips2')}</span>
          </div>
        ),
        cancelText: intl.get('app_confirm_cancel_btn'),
        confirmText: intl.get('app_confirm_confirm_btn'),
        onCancel: () => {
          reject();
          confirmPortal.closeDrawer();
        },
        onConfirm: () => {
          resolve();
          confirmPortal.closeDrawer();
        },
      });
    });
  };

  const submit = async () => {
    if (!selectedRoom.recNo) {
      message.warning(intl.get('bill_credit_can_not_account_msg'));
      return;
    }

    let values;
    try {
      values = await amountForm.validateFields();
    } catch (err) {
      message.warning(intl.get('bill_credit_check_input'));
      return;
    }

    try {
      await showConfirmModal();

      setSubmitLoading(true);

      let couponParams;
      if (couponInfo && couponInfo.couponNo) {
        couponParams = {
          amount: couponInfo.couponFaceValue,
          couponName: couponInfo.couponName,
          couponNo: couponInfo.couponNo,
          couponType: couponInfo.couponType,
          wyuid: couponInfo.wyuid,
        };
      }

      const creditParams = {
        ...values,
        roomId: selectedRoom.roomId,
        roomNo: selectedRoom.roomNo,
        recNo: selectedRoom.recNo,
        amount: parse2Number(parseYuan2Fen(values.amount)),
        resvNo,
        couponDTO: couponParams,
      };

      const res = await CreditApi.appendRoomFee(creditParams);

      if (res && res.status) {
        message.success(intl.get('bill_room_rate_plus_enter_bill_success'));
        closeDrawer();

        // callback parent component
        props.plusCallback();
      } else {
        message.success(res.msg || intl.get('bill_room_rate_plus_enter_bill_fail'));
      }

      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
    }
  };

  const drawerFooter = () => {
    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        <Button type="primary" loading={submitLoading} onClick={() => submit()}>
          {intl.get('bill_credit_account')}
        </Button>
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  return (
    <Drawer
      width="50%"
      title={intl.get('bill_room_rate_plus_add_room_fee')}
      visible={drawerVisible}
      maskClosable={false}
      closable={false}
      destroyOnClose
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <dl className={styles['plus-amount']}>
        <dt className={commonStyles['bill-block-title']}>
          {intl.get('bill_room_rate_plus_add_room_fee')}
        </dt>
        <dd>
          <Form
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 8 }}
            form={amountForm}
            preserve={false}
            className={styles['amount-form']}
          >
            <Form.Item
              className={styles['form-subject']}
              label={intl.get('bill_refound_res_bill_subject')}
              name="subjectCode"
              rules={[
                { required: true, message: intl.get('bill_room_rate_plus_choose_bill_project') },
              ]}
            >
              <Radio.Group onChange={changeSubject}>
                <Space direction="vertical">
                  {ROOM_RATE_PLUS_SUBJECTS.map((item) => (
                    <Radio
                      className={styles['subject-radio-item']}
                      key={item.value}
                      value={item.value}
                    >
                      <>
                        <p>{item.label}</p>
                        <p className={styles['subject-radio-item-extra']}>{item.extra}</p>
                      </>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label={intl.get('bill_credit_amount')}
              name="amount"
              rules={[
                { required: true, message: intl.get('bill_pre_settle_input_amount') },
                {
                  pattern: AMOUNT_REG,
                  message: intl.get('bill_room_rate_plus_input_number_limit'),
                },
              ]}
            >
              <Input
                allowClear
                prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                placeholder={intl.get('bill_pre_settle_input_amount')}
              />
            </Form.Item>
            <Form.Item label={intl.get('bill_room_rate_plus_coupon')} shouldUpdate>
              {() => {
                return renderCouponInfo();
              }}
            </Form.Item>
            <Form.Item label={intl.get('bill_remark')} name="remark">
              <TextArea
                showCount
                rows={4}
                maxLength={10000}
                placeholder={intl.get('bill_credit_account_remark_placeholder')}
              />
            </Form.Item>
          </Form>
        </dd>
      </dl>
      <SelectDiscountCoupon ref={selectCouponRef} />
    </Drawer>
  );
});

const mapStateToProps = (state) => {
  const { selectedRoom } = state.bill;
  return { selectedRoom };
};

export default connect(mapStateToProps, {}, null, { forwardRef: true })(RoomRatePlus);
